import React, { useState } from 'react';
import { ttbAra, ttbStokAra, getPaketListesi, getPaketIcerik, getTtbBaglilar } from '../services/api';

// TTB/TTB_STOK/PAKETLER'de hizmet/stok/paket arayıp checkbox ile seçtirip onKaydet'e teslim eden
// paylaşılan popup. Kabul İşlemleri ve Fiyat Ver ekranları ortak kullanır — ne yapılacağına (DB'ye
// kaydet / hafızada tut) çağıran taraf onKaydet callback'i içinde karar verir, modal bunu bilmez.
//
// Kaynak radio'su: 'hizmet' (TTB) | 'stok' (TTB_STOK) | 'paket' (PAKETLER). Sonuçlar hangi
// kaynaktan geldiğine bakılmaksızın aynı 3 sütunda (Kod/Liste Adı/Hizmet Adı) gösterilebilsin diye
// normalize ediliyor. Paket seçimleri, KAYDET anında paketleriGenislet ile gerçek hizmet/stok
// kalemlerine (PAKETHZMT üzerinden, ADET/FYT_TP1/FIAT1 ile birlikte) açılıyor — onKaydet çağıran
// taraf "paket" diye bir kavram hiç görmüyor, hep düz hizmet/stok listesi alıyor (kullanıcı talebi:
// 2026-08-09).
const KAYNAKLAR = [
  { deger: 'hizmet', etiket: 'Hizmetler' },
  { deger: 'stok', etiket: 'Stoklar' },
  { deger: 'paket', etiket: 'Paketler' },
];

const normalizeSonuc = (kaynak, r) => {
  if (kaynak === 'stok') return { ...r, kaynak, kod: r.id, listeAdi: r.grupAdi, butAdi: r.adi };
  if (kaynak === 'paket') return { ...r, kaynak, kod: r.paketKod, listeAdi: '', butAdi: r.pakAd };
  return { ...r, kaynak, listeAdi: r.listeAdi, butAdi: r.butAdi };
};

export default function HizmetEkleModal({ onKapat, onKaydet, kaydetMetni = 'KAYDET', kaydediliyorMetni = 'Kaydediliyor...' }) {
  const [kaynak, setKaynak] = useState('hizmet');
  const [aramaMetni, setAramaMetni] = useState('');
  const [aramaHata, setAramaHata] = useState('');
  const [araniyor, setAraniyor] = useState(false);
  const [sonuclar, setSonuclar] = useState([]);
  const [seciliMap, setSeciliMap] = useState({}); // { [kaynak:kod]: kayit } — arama/kaynak değişse de seçim korunur
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [kaydetHata, setKaydetHata] = useState('');
  const [paketler, setPaketler] = useState(null); // null = henüz yüklenmedi (küçük liste, arama yok)

  const kaynakDegistir = (yeniKaynak) => {
    setKaynak(yeniKaynak);
    setAramaHata('');

    if (yeniKaynak !== 'paket') { setSonuclar([]); return; }

    if (paketler) { setSonuclar(paketler.map(p => normalizeSonuc('paket', p))); return; }

    setAraniyor(true);
    getPaketListesi()
      .then(res => {
        setPaketler(res.data);
        setSonuclar(res.data.map(p => normalizeSonuc('paket', p)));
      })
      .catch(err => setAramaHata(err.response?.data?.hata || 'Paketler yüklenemedi.'))
      .finally(() => setAraniyor(false));
  };

  const araYap = async () => {
    const deger = aramaMetni.trim();
    if (deger.length < 3) { setAramaHata('Arama için en az 3 karakter girin.'); return; }
    setAramaHata(''); setAraniyor(true);
    try {
      const res = await (kaynak === 'stok' ? ttbStokAra(deger) : ttbAra(deger));
      setSonuclar(res.data.map(r => normalizeSonuc(kaynak, r)));
    } catch (err) {
      setAramaHata(err.response?.data?.hata || 'Arama yapılamadı.');
    } finally {
      setAraniyor(false);
    }
  };

  const anahtar = (kayit) => `${kayit.kaynak}:${kayit.kod}`;

  const toggleSecim = (kayit) => {
    setSeciliMap(prev => {
      const yeni = { ...prev };
      const k = anahtar(kayit);
      if (yeni[k]) delete yeni[k]; else yeni[k] = kayit;
      return yeni;
    });
  };

  const seciliSayisi = Object.keys(seciliMap).length;

  const paketleriGenislet = async (paketSecimleri) => {
    const genisletilmis = [];
    for (const p of paketSecimleri) {
      const res = await getPaketIcerik(p.kod);
      const { hizmetler, stoklar, kalemler } = res.data;
      for (const kalem of kalemler) {
        const kaynakTuru = kalem.tp === 1 ? 'hizmet' : 'stok';
        const kaynakKayit = kalem.tp === 1
          ? hizmetler.find(h => h.kod === kalem.hzmtKod)
          : stoklar.find(s => s.id === kalem.hzmtKod);
        if (!kaynakKayit) continue;
        genisletilmis.push({
          ...normalizeSonuc(kaynakTuru, kaynakKayit),
          adet: kalem.adet,
          fytTp1: kalem.fytTp1,
          fiat1: kalem.fiat1,
        });
      }
    }
    return genisletilmis;
  };

  // Elle seçilen (paketten gelmemiş) hizmetlere otomatik bağlı gelen nesneleri (TTB_STOK_BAGLA)
  // ekler — SADECE bir seviye, bağlı nesnenin kendi bağlıları ayrıca genişletilmez (kullanıcı
  // talebi: 2026-08-09, "bağlının bağlısı gibi bir alt döngüye girmiyoruz").
  const baglilariGenislet = async (hizmetSecimleri) => {
    const genisletilmis = [];
    for (const h of hizmetSecimleri) {
      const res = await getTtbBaglilar(h.kod);
      const { hizmetler, stoklar, kalemler } = res.data;
      for (const kalem of kalemler) {
        const kaynakTuru = kalem.ne === 1 ? 'hizmet' : 'stok';
        const kaynakKayit = kalem.ne === 1
          ? hizmetler.find(x => x.kod === kalem.stokKod)
          : stoklar.find(x => x.id === kalem.stokKod);
        if (!kaynakKayit) continue;
        genisletilmis.push({
          ...normalizeSonuc(kaynakTuru, kaynakKayit),
          adet: kalem.mik,
          ozelFiat: kalem.ozelFiat,
          fiat1: kalem.fiat1,
        });
      }
    }
    return genisletilmis;
  };

  const kaydet = async () => {
    if (seciliSayisi === 0) { setKaydetHata('En az bir hizmet seçmelisiniz.'); return; }
    setKaydetHata(''); setKaydediliyor(true);
    try {
      const secilenler = Object.values(seciliMap);
      const paketSecimleri = secilenler.filter(s => s.kaynak === 'paket');
      const hizmetSecimleri = secilenler.filter(s => s.kaynak === 'hizmet');
      const digerSecimler = secilenler.filter(s => s.kaynak !== 'paket');
      const paketIcerikleri = paketSecimleri.length > 0 ? await paketleriGenislet(paketSecimleri) : [];
      const baglıKalemler = hizmetSecimleri.length > 0 ? await baglilariGenislet(hizmetSecimleri) : [];
      await onKaydet([...digerSecimler, ...paketIcerikleri, ...baglıKalemler]);
    } catch (err) {
      setKaydetHata(err.response?.data?.hata || 'Kaydedilemedi.');
    } finally {
      setKaydediliyor(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: 520, maxWidth: '92vw', maxHeight: '86vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 14, color: 'var(--med-dark)' }}>Hizmet Ekle</h3>
          <button onClick={onKapat} className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }}>✕</button>
        </div>

        <div style={{ display: 'flex', gap: 16, marginBottom: 10 }}>
          {KAYNAKLAR.map(k => (
            <label key={k.deger} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, cursor: 'pointer' }}>
              <input type="radio" name="kaynak" checked={kaynak === k.deger} onChange={() => kaynakDegistir(k.deger)} />
              {k.etiket}
            </label>
          ))}
        </div>

        {kaynak !== 'paket' && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input
              type="text"
              placeholder="Hizmet adı yazın (en az 3 karakter)..."
              value={aramaMetni}
              onChange={e => setAramaMetni(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') araYap(); }}
              autoFocus
              style={{ flex: 1 }}
            />
            <button onClick={araYap} disabled={araniyor} className="btn btn-primary">
              {araniyor ? '...' : '🔍 Ara'}
            </button>
          </div>
        )}
        {aramaHata && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 8 }}>{aramaHata}</div>}

        <div style={{ flex: 1, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 10, minHeight: 120 }}>
          {araniyor ? (
            <div className="loading">{kaynak === 'paket' ? 'Paketler yükleniyor...' : 'Aranıyor...'}</div>
          ) : sonuclar.length === 0 ? (
            <div className="empty">
              {kaynak === 'paket'
                ? 'Kayıtlı paket bulunamadı.'
                : (aramaMetni.trim().length >= 3 ? 'Sonuç bulunamadı' : 'Aramak için yukarıdan yazıp Ara\'ya basın')}
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '28px 70px 110px 1fr', gap: 10, padding: '6px 12px', background: '#f8faf9', borderBottom: '1px solid var(--border)', fontSize: 11, color: 'var(--text-muted)', fontWeight: 500, textAlign: 'left' }}>
                <span></span>
                <span>Kod</span>
                <span>Liste Adı</span>
                <span>Hizmet Adı</span>
              </div>
              {sonuclar.map(s => (
                <label
                  key={anahtar(s)}
                  style={{ display: 'grid', gridTemplateColumns: '28px 70px 110px 1fr', gap: 10, alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 13, cursor: 'pointer', textAlign: 'left' }}
                >
                  <input type="checkbox" checked={!!seciliMap[anahtar(s)]} onChange={() => toggleSecim(s)} />
                  <span>{s.kod}</span>
                  <span>{s.listeAdi}</span>
                  <span>{s.butAdi}</span>
                </label>
              ))}
            </>
          )}
        </div>

        {kaydetHata && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 8 }}>{kaydetHata}</div>}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{seciliSayisi} hizmet seçildi</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onKapat} className="btn btn-secondary">Vazgeç</button>
            <button onClick={kaydet} disabled={kaydediliyor || seciliSayisi === 0} className="btn btn-primary">
              {kaydediliyor ? kaydediliyorMetni : kaydetMetni}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
